import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PhotoDemo, { BoxBrackets } from "./PhotoDemo";
import WaitlistForm from "./WaitlistForm";
import SiteFrame from "./SiteFrame";
import { EAGLE_DARK, PERSONAL_CHIPS, PERSONAL_HERO, PERSONAL_PHOTOS } from "./images";
import { APP_SCREENS, PhoneFrame, appShot, useAppShot, type AppScreen } from "./appScreens";
import { STUDIO_PATH } from "./paths";
import { CameraIcon, ProductEyebrow } from "./products";
import { AppStoreButton } from "./PathCards";
import { AMBER_DISPLAY, BAND, BTN_OUTLINE_MD, BTN_PRIMARY_MD, H1, H2, HERO_FIGURE, HERO_TEXT, SECTION_Y, WRAP } from "./ui";
import { cn } from "@/lib/utils";
import type { joinWaitlist } from "@/lib/personal/waitlist";

const FEATURES = ["grouped", "things", "send", "receive", "private", "control"] as const;
const STEPS = ["1", "2", "3"] as const;
/** Each app screen's one-line caption, from the page's feature copy (home.personal.*). */
const SCREEN_CAPTIONS: Record<AppScreen, string> = {
  people: "features.grouped.title",
  search: "features.things.title",
  person: "how.step2Title",
  share: "features.send.title",
  detail: "features.private.title",
};

/**
 * /personal: Photohawk Personal, the phone app for your own photos (iPhone first, Android next), coming soon. The
 * App Store once its listing is live (PERSONAL_APP_STORE_URL), the waitlist until then: there is nothing to sign up
 * for on the web, and you sign in inside the app. In the site's one light look (white sections and the dashboard's muted band). No app
 * pricing anywhere until it's decided (owner, 19 Oct 2026); the waitlist is #waitlist.
 */
const PersonalPage = ({ submitWaitlist }: { submitWaitlist?: typeof joinWaitlist }) => {
  const { t, i18n } = useTranslation();
  const k = (key: string, opts?: Record<string, string | number>): string => t(`home.personal.${key}`, opts ?? {});
  const peopleShot = useAppShot("people");

  return (
    <SiteFrame page="app" path="app">
      {/* HERO */}
      <section aria-labelledby="personal-title">
        <div className={`${WRAP} flex flex-wrap items-center gap-x-14 gap-y-10 pb-[clamp(56px,7vw,96px)] pt-[clamp(32px,5vw,80px)]`}>
          <div className={HERO_TEXT}>
            <ProductEyebrow product="personal" text={t("home.doors.personal.for")} />
            <h1 id="personal-title" className={H1}>
              {k("title")} <span style={{ color: AMBER_DISPLAY }}>{k("titleHighlight")}</span>
            </h1>
            <p className="m-0 max-w-[32em] text-[clamp(17px,1.5vw,20px)] leading-relaxed text-muted-foreground">{k("subtitle")}</p>
            <WaitlistForm submit={submitWaitlist} />
            <div className="flex flex-wrap items-center gap-x-4 gap-y-3" data-testid="personal-get-app">
              <AppStoreButton />
              <span className="flex flex-col gap-0.5 text-sm">
                <strong className="font-semibold text-foreground">{t("home.doors.personal.platforms")}</strong>
                <span className="text-muted-foreground">{t("home.doors.personal.signIn")}</span>
              </span>
            </div>
          </div>
          <figure className={`${HERO_FIGURE} pb-[34px]`}>
            <div className="relative aspect-[3/2] w-full overflow-hidden rounded-xl shadow-lg">
              <img
                src={PERSONAL_HERO.src}
                srcSet={PERSONAL_HERO.srcSet}
                sizes="(min-width: 1024px) 640px, 100vw"
                width={PERSONAL_HERO.width}
                height={PERSONAL_HERO.height}
                alt={k("heroAlt")}
                className="block h-full w-full object-cover"
              />
              {PERSONAL_HERO.faces.map((f) => (
                <span key={f.name}>
                  <BoxBrackets box={f.box} arm="28%" delay={f.delay} />
                  <span
                    aria-hidden="true"
                    className="absolute rounded bg-[#EA8A23] px-[9px] py-[3px] text-[13px] font-bold text-[#121721]"
                    style={{ left: `${f.box[0]}%`, top: `${f.box[1] + f.box[3] + 1.6}%` }}
                  >
                    {f.name}
                  </span>
                </span>
              ))}
            </div>
            {/* The app, over the photo's left edge: the People screen, where Tom and Ana end up. */}
            <div className="absolute bottom-0 left-3 w-[23%] max-w-[190px] -rotate-[4deg] sm:w-[27%] lg:left-[-3%]" data-testid="personal-hero-phone">
              <PhoneFrame shot={peopleShot} alt={k("screens.alt.people")} sizes="(min-width: 1024px) 180px, 27vw" eager />
            </div>
            <figcaption className="absolute bottom-0 right-3 flex items-center gap-2.5 rounded-xl border border-border bg-card py-2.5 pl-2.5 pr-3.5 text-foreground shadow-lg">
              <img src={EAGLE_DARK} alt="" width={26} height={27} className="h-[27px] w-[26px] object-contain" />
              <span className="text-sm font-semibold">{k("heroCaption")}</span>
            </figcaption>
          </figure>
        </div>
      </section>

      {/* FEATURES */}
      <section aria-labelledby="personal-features" className={`${BAND} border-y border-border`}>
        <div className={`${WRAP} ${SECTION_Y} flex flex-col gap-9`}>
        <h2 id="personal-features" className={`${H2} max-w-[16em]`}>
          {k("features.heading")}
        </h2>
        <ul className="m-0 grid list-none grid-cols-1 gap-px overflow-hidden rounded-2xl border border-border bg-border p-0 shadow-sm sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map((f) => (
            <li key={f} className="flex flex-col gap-2.5 bg-card p-7">
              <h3 className="m-0 font-display text-[21px] font-semibold">{k(`features.${f}.title`)}</h3>
              <p className="m-0 text-[15px] leading-relaxed text-muted-foreground">{k(`features.${f}.text`)}</p>
            </li>
          ))}
        </ul>
        </div>
      </section>

      {/* SEE IT IN THE APP: real screens, a scroller on phones and tablets, a row of five from 1024 px */}
      <section aria-labelledby="personal-screens" className="pt-[clamp(64px,8vw,104px)]" data-testid="personal-screens">
        <div className={`${WRAP} flex flex-col gap-9`}>
          <h2 id="personal-screens" className={H2}>
            {k("screens.heading")}
          </h2>
          <ul
            className="-mx-[clamp(16px,4vw,56px)] my-0 flex list-none snap-x snap-mandatory scroll-px-[clamp(16px,4vw,56px)] gap-5 overflow-x-auto px-[clamp(16px,4vw,56px)] pb-4 pt-1 lg:mx-0 lg:grid lg:grid-cols-5 lg:gap-[clamp(16px,2vw,32px)] lg:overflow-visible lg:px-0"
            tabIndex={0}
            aria-label={k("screens.heading")}
          >
            {APP_SCREENS.map((screen) => (
              <li key={screen} className="flex w-[clamp(170px,46vw,220px)] flex-none snap-start flex-col gap-4 lg:w-auto" data-testid={`app-screen-${screen}`}>
                <PhoneFrame
                  shot={appShot(screen, i18n.language)}
                  alt={k(`screens.alt.${screen}`)}
                  sizes="(min-width: 1024px) 230px, 220px"
                />
                <p className="m-0 font-display text-[17px] font-semibold leading-snug">{k(SCREEN_CAPTIONS[screen])}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* DEMO */}
      <PhotoDemo
        idPrefix="personal-demo"
        eyebrow={k("demo.eyebrow")}
        heading={k("demo.heading")}
        chips={PERSONAL_CHIPS.map((id) => ({ id, label: k(`demo.chips.${id}`) }))}
        chipsLabel={k("demo.chipsLabel")}
        initial="dog"
        photos={PERSONAL_PHOTOS}
        altOf={(p) => k(`demo.alt.${p.id}`)}
        result={(chip, count, total) => k("demo.result", { label: chip.label, count, total })}
        chipShape="pill"
      />

      {/* HOW IT WORKS (no pricing: it's undecided) */}
      <section aria-labelledby="personal-how" className={`${BAND} border-y border-border`}>
        <div className={`${WRAP} ${SECTION_Y} flex flex-wrap items-start gap-x-16 gap-y-12`}>
          <div className="flex min-w-0 flex-[1_1_420px] flex-col gap-7">
            <h2 id="personal-how" className={H2}>
              {k("how.heading")}
            </h2>
            <ol className="m-0 flex list-none flex-col gap-[22px] p-0">
              {STEPS.map((n) => (
                <li key={n} className="flex gap-[18px]">
                  <span className="flex-none font-display text-lg font-bold text-accent-foreground" aria-hidden="true">
                    0{n}
                  </span>
                  <span className="flex flex-col gap-1">
                    <strong className="text-lg">{k(`how.step${n}Title`)}</strong>
                    <span className="text-[15px] leading-relaxed text-muted-foreground">{k(`how.step${n}Text`)}</span>
                  </span>
                </li>
              ))}
            </ol>
            <a href="#waitlist" className={cn(BTN_PRIMARY_MD, "self-start text-[15px]")}>
              {t("home.joinWaitlist")}
            </a>
          </div>
        </div>
      </section>

      {/* CROSS-LINK: the other path */}
      <section aria-labelledby="personal-cross">
        <div className={`${WRAP} flex flex-wrap items-center justify-between gap-x-8 gap-y-3.5 py-7`}>
          <p className="m-0 flex items-center gap-3.5 text-base leading-normal">
            <span className="flex-none text-accent-foreground">
              <CameraIcon size={26} />
            </span>
            <span>
              <strong id="personal-cross">{k("cross.title")}</strong> {k("cross.text")}
            </span>
          </p>
          <Link to={STUDIO_PATH} className={BTN_OUTLINE_MD}>
            {k("cross.button")}
          </Link>
        </div>
      </section>
    </SiteFrame>
  );
};

export default PersonalPage;
